/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export const isObject = (item: any) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

/**
 * Deep merge two objects.
 * @param target
 * @param sources
 * @deprecated - this function mutates the input, do not use this
 */
export const unsafe_deepMerge = <T extends Partial<T>>(
  target: T,
  ...sources: any
): T => {
  if (!sources.length) {
    return target;
  }

  const source = sources.shift();

  // This line is already covered, but since it's recursive, jest will complain that it is not covered
  /* istanbul ignore next */
  if (isObject(target) && isObject(source)) {
    for (const _key in source) {
      const key = _key as keyof T;
      /* istanbul ignore next */
      if (source.hasOwnProperty(key)) {
        if (isObject(source[key])) {
          if (!target[key]) {
            Object.assign(target, { [key]: {} });
          }
          unsafe_deepMerge(target[key], source[key]);
        } else {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }
  }

  return unsafe_deepMerge(target, ...sources);
};

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

/**
 * Deep merges two objects without modifying the input
 * @param target
 * @param source
 */
export const deepMerge = <T extends Partial<T>, U extends RecursivePartial<T>>(
  target: T,
  source: U
): T & U => {
  let output = Object.assign({}, target);

  // This line is already covered, but since it's recursive, jest will complain that it is not covered
  /* istanbul ignore next */
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      const _key: any = key as keyof (T | U);
      if (isObject((source as any)[_key])) {
        if (!(_key in target)) Object.assign(output, { [_key]: (source as any)[_key] });
        else (output as any)[_key] = deepMerge((target as any)[_key], (source as any)[_key]);
      } else {
        Object.assign(output, { [_key]: (source as any)[_key] });
      }
    });
  }
  return output as T & U;
};
